import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Plus, User, Clock, Scissors } from "lucide-react";
import { format, addDays, addWeeks, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay, isSameMonth, parseISO, differenceInCalendarDays } from "date-fns";
import { norm } from "@/lib/normalize";

const HOURS = Array.from({ length: 13 }, (_, i) => 9 + i); // 08..19
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CATEGORY_CLASS = {
  haircut: "bg-[rgba(221,242,199,0.15)] border-[rgba(221,242,199,0.5)] text-[#B7E38F]",
  hair: "bg-[rgba(221,242,199,0.15)] border-[rgba(221,242,199,0.5)] text-[#B7E38F]",
  color: "bg-[rgba(229,222,251,0.15)] border-[rgba(229,222,251,0.5)] text-[#CFC5F5]",
  coloring: "bg-[rgba(229,222,251,0.15)] border-[rgba(229,222,251,0.5)] text-[#CFC5F5]",
  treatment: "bg-[rgba(207,230,240,0.15)] border-[rgba(207,230,240,0.5)] text-[#B0D6E7]",
  spa: "bg-[rgba(207,230,240,0.15)] border-[rgba(207,230,240,0.5)] text-[#B0D6E7]",
  facial: "bg-[rgba(207,230,240,0.15)] border-[rgba(207,230,240,0.5)] text-[#B0D6E7]",
  nails: "bg-[rgba(255,224,233,0.15)] border-[rgba(255,224,233,0.5)] text-[#F1BFCC]",
  nail: "bg-[rgba(255,224,233,0.15)] border-[rgba(255,224,233,0.5)] text-[#F1BFCC]",
  massage: "bg-[rgba(255,236,199,0.15)] border-[rgba(255,236,199,0.5)] text-[#FFD584]",
  service: "bg-[rgba(197,160,89,0.1)] border-[rgba(197,160,89,0.35)] text-gold",
};
const catClass = (c) => CATEGORY_CLASS[(c || "service").toLowerCase()] || CATEGORY_CLASS.service;
const CATEGORY_LEGEND = [
  { key: "haircut", label: "Haircut" },
  { key: "coloring", label: "Coloring" },
  { key: "treatment", label: "Treatment" },
  { key: "nails", label: "Nails" },
  { key: "massage", label: "Massage" },
];
const STATUS_OPTS = [
  { key: "scheduled", label: "Scheduled" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const hhmmToMin = (t) => {
  if (!t || !t.includes(":")) return 0;
  const [h, m] = t.split(":");
  return parseInt(h, 10) * 60 + parseInt(m, 10);
};
const toDateSafe = (s) => {
  try { return parseISO(s); } catch { return null; }
};

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdminOrStaff = norm(user.roleName) === "admin" || norm(user.roleName) === "staff" || norm(user.roleName) === "customer"
  const [view, setView] = useState("week"); // day | week | month
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [staffFilter, setStaffFilter] = useState("all");
  const [statusFilters, setStatusFilters] = useState({ scheduled: true, completed: true, cancelled: false });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, s, sv] = await Promise.all([
          api.get("/appointments"),
          api.get("/staff"),
          api.get("/services"),
        ]);
        setAppts(a.data);
        setStaff(s.data);
        setServices(sv.data);
      } catch { toast.error("Failed to load calendar"); }
    })();
  }, []);

  const serviceMap = useMemo(() => {
    const m = {};
    services.forEach((s) => { m[s.id] = s; });
    return m;
  }, [services]);

  const filtered = useMemo(() => {
    return appts.filter((a) => {
      if (!statusFilters[norm(a.status)]) return false;
      if (staffFilter !== "all" && a.staffId !== staffFilter) return false;
      return true;
    });
  }, [appts, staffFilter, statusFilters]);

  const nav = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addMonths(cursor, dir));
  };
  const goToday = () => setCursor(new Date());

  const rangeLabel = useMemo(() => {
    if (view === "day") return format(cursor, "EEEE, d MMMM yyyy");
    if (view === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      const e = endOfWeek(cursor, { weekStartsOn: 1 });
      return `${format(s, "d MMM")} — ${format(e, "d MMM yyyy")}`;
    }
    return format(cursor, "MMMM yyyy");
  }, [view, cursor]);

  const openBook = (date, time, staffId) => {
    if (!isAdminOrStaff) return;
    const params = new URLSearchParams();
    if (date) params.set("date", format(date, "yyyy-MM-dd"));
    if (time) params.set("time", time);
    if (staffId && staffId !== "all") params.set("staff", staffId);
    navigate(`/dashboard/book?${params.toString()}`);
  };

  const toggleStatus = (k) => setStatusFilters((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Team Schedule</div>
          <h1 className="font-heading text-4xl text-white">Calendar</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="flex items-center bg-emerald-surface border border-gold-subtle rounded-full p-1" data-testid="view-toggle">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`view-${v}`}
                className={`px-4 py-1.5 rounded-full text-xs capitalize transition ${
                  view === v ? "bg-gold text-emerald-950" : "text-emerald-muted hover:text-white"
                }`}
              >{v}</button>
            ))}
          </div>
          {/* Date nav */}
          <div className="flex items-center bg-emerald-surface border border-gold-subtle rounded-full p-1">
            <button onClick={() => nav(-1)} data-testid="cal-prev" className="p-1.5 rounded-full text-emerald-muted hover:text-gold"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={goToday} data-testid="cal-today" className="px-3 py-1 text-xs text-emerald-muted hover:text-gold">Today</button>
            <button onClick={() => nav(1)} data-testid="cal-next" className="p-1.5 rounded-full text-emerald-muted hover:text-gold"><ChevronRight className="w-4 h-4" /></button>
          </div>
          {isAdminOrStaff && (
            <button onClick={() => openBook(cursor, null, staffFilter)} data-testid="cal-new-btn"
              className="gold-button inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm">
              <Plus className="w-3.5 h-3.5" /> New
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="text-white font-heading text-2xl" data-testid="cal-range-label">{rangeLabel}</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={staffFilter} onValueChange={setStaffFilter}>
            <SelectTrigger data-testid="staff-filter" className="w-52 bg-emerald-surface border-gold-subtle text-sm h-9">
              <SelectValue placeholder="All staff" />
            </SelectTrigger>
            <SelectContent className="bg-emerald-surface border-gold-subtle text-white">
              <SelectItem value="all">All staff</SelectItem>
              {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.firstName} {s.lastName || ""}</SelectItem>)}
            </SelectContent>
          </Select>
          {STATUS_OPTS.map((s) => (
            <button
              key={s.key}
              data-testid={`status-filter-${s.key}`}
              onClick={() => toggleStatus(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                statusFilters[s.key]
                  ? "bg-[rgba(197,160,89,0.15)] border-gold-subtle text-gold"
                  : "border-gold-subtle text-emerald-muted hover:text-white"
              }`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap text-xs">
        {CATEGORY_LEGEND.map((c) => (
          <div key={c.key} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${catClass(c.key)}`}>
            <span className="w-2 h-2 rounded-full bg-current opacity-70" /> {c.label}
          </div>
        ))}
      </div>

      {view === "day" && <DayView date={cursor} appts={filtered} serviceMap={serviceMap} onSelect={setSelected} onEmpty={openBook} isStaff={isAdminOrStaff} staffFilter={staffFilter} />}
      {view === "week" && <WeekView start={startOfWeek(cursor, { weekStartsOn: 1 })} appts={filtered} serviceMap={serviceMap} onSelect={setSelected} onEmpty={openBook} isStaff={isAdminOrStaff} staffFilter={staffFilter} />}
      {view === "month" && <MonthView cursor={cursor} appts={filtered} serviceMap={serviceMap} onSelect={setSelected} onEmpty={openBook} isStaff={isAdminOrStaff} setCursor={setCursor} setView={setView} />}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-md" data-testid="appt-detail-modal">
          {selected && (() => {
            const svc = serviceMap[selected.serviceId] || selected.service || {};
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">{svc.name || "Appointment"}</DialogTitle>
                  <DialogDescription className="text-emerald-muted">
                    {selected.appointmentDate} at {selected.startTime}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-white"><User className="w-4 h-4 text-emerald-muted" /> {selected.customerName || "—"}</div>
                  <div className="flex items-center gap-2 text-white"><Scissors className="w-4 h-4 text-emerald-muted" /> {selected.staffName|| "—"}</div>
                  <div className="flex items-center gap-2 text-white"><Clock className="w-4 h-4 text-emerald-muted" /> {svc.duration_min ? `${svc.duration_min} min` : ""} · ${selected.price}</div>
                  <div className="flex items-center gap-2"><CalIcon className="w-4 h-4 text-emerald-muted" /> <span className="text-emerald-muted">Status:</span> <span className="text-gold capitalize">{selected.status}</span></div>
                  {selected.remarks && <div className="p-3 rounded-md bg-emerald-elev/60 text-emerald-muted text-xs">{selected.remarks}</div>}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------ Day View ------------
function DayView({ date, appts, serviceMap, onSelect, onEmpty, isStaff, staffFilter }) {
  const iso = format(date, "yyyy-MM-dd");
  const dayAppts = appts.filter((a) => a.appointmentDate === iso);
  return (
    <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden" data-testid="day-view">
      <div className="grid grid-cols-[80px_1fr]">
        <div className="border-r border-gold-subtle bg-emerald-elev/40" />
        <div className="px-4 py-3 text-white font-medium text-sm border-b border-gold-subtle">
          {format(date, "EEEE, d MMM")}
        </div>
        {HOURS.map((h) => (
          <HourRow
            key={h}
            hour={h}
            appts={dayAppts.filter((a) => hhmmToMin(a.startTime) >= h * 60 && hhmmToMin(a.startTime) < (h + 1) * 60)}
            serviceMap={serviceMap}
            onSelect={onSelect}
            onEmpty={() => isStaff && onEmpty(date, `${String(h).padStart(2, "0")}:00`, staffFilter)}
          />
        ))}
      </div>
    </div>
  );
}

function HourRow({ hour, appts, serviceMap, onSelect, onEmpty }) {
  return (
    <>
      <div className="border-r border-t border-gold-subtle text-emerald-muted text-xs px-3 py-4 bg-emerald-elev/40">
        {String(hour).padStart(2, "0")}:00
      </div>
      <div
        className="border-t border-gold-subtle min-h-[64px] px-2 py-2 cursor-pointer hover:bg-emerald-elev/20 transition"
        onClick={onEmpty}
        data-testid={`day-slot-${hour}`}
      >
        <div className="flex flex-wrap gap-2">
          {appts.map((a) => {
            const svc = serviceMap[a.serviceId] || {};
            return (
              <button
                key={a.id}
                onClick={(e) => { e.stopPropagation(); onSelect(a); }}
                data-testid={`appt-${a.id}`}
                className={`text-left rounded-lg border px-3 py-2 min-w-[200px] max-w-[280px] hover:opacity-90 transition ${catClass(svc.category)}`}
              >
                <div className="font-medium text-sm">{(a.startTime || "").slice(0, 5)} · {a.customerName || "Guest"}</div>
                <div className="text-[11px] opacity-80 truncate">{a.serviceName || svc.name} · {a.staffName}</div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ------------ Week View ------------
function WeekView({ start, appts, serviceMap, onSelect, onEmpty, isStaff, staffFilter }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden" data-testid="week-view">
      <div className="grid grid-cols-[80px_repeat(7,1fr)]">
        <div className="border-r border-gold-subtle bg-emerald-elev/40" />
        {days.map((d, i) => (
          <div key={i} className={`px-3 py-3 text-center border-l border-b border-gold-subtle ${isSameDay(d, new Date()) ? "bg-[rgba(197,160,89,0.08)]" : ""}`}>
            <div className="text-[10px] text-emerald-muted uppercase tracking-widest">{WEEK_DAYS[i]}</div>
            <div className={`text-lg mt-0.5 ${isSameDay(d, new Date()) ? "text-gold" : "text-white"}`}>{format(d, "d")}</div>
          </div>
        ))}
        {HOURS.map((h) => (
          <WeekHourRow
            key={h}
            hour={h}
            days={days}
            appts={appts}
            serviceMap={serviceMap}
            onSelect={onSelect}
            onEmpty={onEmpty}
            isStaff={isStaff}
            staffFilter={staffFilter}
          />
        ))}
      </div>
    </div>
  );
}

function WeekHourRow({ hour, days, appts, serviceMap, onSelect, onEmpty, isStaff, staffFilter }) {
  return (
    <>
      <div className="border-r border-t border-gold-subtle text-emerald-muted text-xs px-3 py-4 bg-emerald-elev/40">
        {String(hour).padStart(2, "0")}:00
      </div>
      {days.map((d, i) => {
        const iso = format(d, "yyyy-MM-dd");
        const cell = appts.filter((a) => a.appointmentDate === iso && hhmmToMin(a.startTime) >= hour * 60 && hhmmToMin(a.startTime) < (hour + 1) * 60);
        return (
          <div
            key={i}
            onClick={() => isStaff && onEmpty(d, `${String(hour).padStart(2, "0")}:00`, staffFilter)}
            className="border-l border-t border-gold-subtle min-h-[54px] p-1 cursor-pointer hover:bg-emerald-elev/20 transition"
            data-testid={`week-slot-${iso}-${hour}`}
          >
            <div className="space-y-1">
              {cell.map((a) => {
                const svc = serviceMap[a.serviceId] || {};
                return (
                  <button
                    key={a.id}
                    onClick={(e) => { e.stopPropagation(); onSelect(a); }}
                    data-testid={`appt-${a.id}`}
                    className={`w-full text-left rounded-md border px-1.5 py-1 hover:opacity-90 transition ${catClass(svc.category)}`}
                  >
                    <div className="text-[11px] font-medium truncate">{(a.startTime || "").slice(0, 5)} · {a.customerName || "Guest"}</div>
                    <div className="text-[10px] opacity-80 truncate">{a.serviceName || svc.name}</div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ------------ Month View ------------
function MonthView({ cursor, appts, serviceMap, onSelect, onEmpty, isStaff, setCursor, setView }) {
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const totalCells = differenceInCalendarDays(gridEnd, gridStart) + 1;
  const days = Array.from({ length: totalCells }, (_, i) => addDays(gridStart, i));
  return (
    <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden" data-testid="month-view">
      <div className="grid grid-cols-7 bg-emerald-elev/40">
        {WEEK_DAYS.map((d) => (
          <div key={d} className="px-3 py-2 text-center text-[10px] text-emerald-muted uppercase tracking-widest border-b border-gold-subtle">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const iso = format(d, "yyyy-MM-dd");
          const cellAppts = appts.filter((a) => a.appointmentDate === iso);
          const inMonth = isSameMonth(d, cursor);
          const today = isSameDay(d, new Date());
          const more = cellAppts.length - 3;
          return (
            <div
              key={i}
              className={`min-h-[110px] p-2 border-t border-l border-gold-subtle cursor-pointer hover:bg-emerald-elev/20 transition ${
                inMonth ? "" : "opacity-40"
              } ${today ? "bg-[rgba(197,160,89,0.05)]" : ""}`}
              onClick={() => {
                if (cellAppts.length > 0) {
                  setCursor(d);
                  setView("day");
                } else if (isStaff) {
                  onEmpty(d, null, "all");
                }
              }}
              data-testid={`month-cell-${iso}`}
            >
              <div className={`text-xs font-medium mb-1 ${today ? "text-gold" : "text-white"}`}>{format(d, "d")}</div>
              <div className="space-y-1">
                {cellAppts.slice(0, 3).map((a) => {
                  const svc = serviceMap[a.serviceId] || {};
                  return (
                    <button
                      key={a.id}
                      onClick={(e) => { e.stopPropagation(); onSelect(a); }}
                      data-testid={`appt-${a.id}`}
                      className={`w-full text-left truncate rounded px-1.5 py-0.5 text-[10px] border ${catClass(svc.category)}`}
                    >
                      {(a.startTime || "").slice(0, 5)} {a.customerName || "Guest"}
                    </button>
                  );
                })}
                {more > 0 && <div className="text-[10px] text-gold">+{more} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
